import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Building2, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface Club {
  id: string;
  name: string;
  city?: string;
  logo_url: string | null;
  has_active_subscription?: boolean;
}

interface ClubSearchSelectProps {
  clubs: Club[];
  value: string;
  onChange: (clubId: string) => void;
  placeholder?: string;
}

export const ClubSearchSelect = ({ 
  clubs, 
  value, 
  onChange,
  placeholder = "Zoek je club..."
}: ClubSearchSelectProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Filter clubs with active subscription and match search query
  const filteredClubs = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return clubs
      .filter(club => club.has_active_subscription !== false)
      .filter(club => {
        const nameLower = club.name.toLowerCase();
        if (nameLower.includes("demo") || nameLower.includes("test")) return false;
        return true;
      })
      .filter(club => {
        if (!query) return true;
        return (
          club.name.toLowerCase().includes(query) ||
          (club.city && club.city.toLowerCase().includes(query))
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clubs, searchQuery]);

  const selectedClub = clubs.find(c => c.id === value);

  return (
    <div className="space-y-2">
      <Label htmlFor="club-search">Club *</Label>
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="club-search"
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={selectedClub ? selectedClub.name : placeholder}
            className="pl-10"
          />
        </div>
        
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg">
            <ScrollArea className="max-h-[200px]">
              {filteredClubs.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {searchQuery ? "Geen clubs gevonden" : "Geen actieve clubs beschikbaar"}
                </div>
              ) : (
                <div className="p-1">
                  {filteredClubs.map((club) => (
                    <button
                      key={club.id}
                      type="button"
                      onClick={() => {
                        onChange(club.id);
                        setSearchQuery("");
                        setIsOpen(false);
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 p-2 rounded-md text-left hover:bg-accent transition-colors",
                        value === club.id && "bg-accent"
                      )}
                    >
                      {club.logo_url ? (
                        <img 
                          src={club.logo_url} 
                          alt={club.name}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{club.name}</p>
                        {club.city && (
                          <p className="text-xs text-muted-foreground">{club.city}</p>
                        )}
                      </div>
                      {value === club.id && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </div>
      
      {selectedClub && !isOpen && (
        <div className="flex items-center gap-2 p-2 bg-accent/30 rounded-md">
          {selectedClub.logo_url ? (
            <img 
              src={selectedClub.logo_url} 
              alt={selectedClub.name}
              className="h-6 w-6 rounded-full object-cover"
            />
          ) : (
            <Building2 className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium">{selectedClub.name}</span>
          <button
            type="button"
            onClick={() => {
              onChange("");
              setIsOpen(true);
            }}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground"
          >
            Wijzigen
          </button>
        </div>
      )}
    </div>
  );
};
